import React, { useState, useEffect } from 'react';
import { auth } from '../lib/firebase';
import { 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider
} from 'firebase/auth';
import { Trophy, Lock, ChevronRight, Star } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface LoginManagerProps {
  onViewOnlyLogin: (password: string) => void;
  onCustomLogin?: (user: any) => void;
}

export default function LoginManager({ onViewOnlyLogin }: LoginManagerProps) {
  const [viewOnlyPass, setViewOnlyPass] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    getRedirectResult(auth).then((result) => {
      if (result) {
         toast.success('Biometric scan complete. Welcome aboard.');
      }
    }).catch((error) => {
      console.error("Redirect error:", error);
      if (error.code === 'auth/unauthorized-domain' || error.message?.includes('unauthorized-domain')) {
        toast.error('UNAUTHORIZED DOMAIN: This domain (' + window.location.hostname + ') must be authorized in Firebase Console -> Auth -> Settings.', { duration: 10000 });
      } else if (error.code === 'auth/missing-initial-state' || error.message?.includes('missing initial state') || error.code === 'auth/redirect-cancelled') {
        try {
          if (window.indexedDB) {
            window.indexedDB.deleteDatabase('firebaseLocalStorageDb');
            console.log('Cleared firebase auth cache to resolve redirect loop');
          }
        } catch (e) {}
        toast.error('Login failed: ' + (error.message || 'Unknown error'));
      } else {
        toast.error('Login failed: ' + (error.message || 'Unknown error'));
      }
    });
  }, []);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      toast.success('Biometric scan complete. Welcome aboard.');
      setIsLoading(false);
    } catch (error: any) {
      console.error(error);
      if (error.code === 'auth/popup-closed-by-user') {
        setIsLoading(false);
        toast.info('Sign-in cancelled or window closed. Click again when ready to proceed.');
      } else if (error.code === 'auth/unauthorized-domain' || error.message?.includes('unauthorized-domain')) {
        toast.error(`UNAUTHORIZED DOMAIN: ${window.location.hostname} is not authorized for Google login in Firebase Console. Please contact Admin.`, { duration: 10000 });
        setIsLoading(false);
      } else if (error.code === 'auth/popup-blocked' || error.code === 'auth/missing-initial-state' || error.message?.includes('missing initial state')) {
        // Auto-clear cache to prevent loops before redirecting
        try { if (window.indexedDB) window.indexedDB.deleteDatabase('firebaseLocalStorageDb'); } catch (e) {}
        
        toast.error('Pop-up restricted. Attempting safe redirect...', { duration: 3000 });
        const provider = new GoogleAuthProvider();
        signInWithRedirect(auth, provider).catch(e => {
           setIsLoading(false);
           if (e.code === 'auth/unauthorized-domain' || e.message?.includes('unauthorized-domain')) {
             toast.error(`UNAUTHORIZED DOMAIN: ${window.location.hostname} is not authorized in Firebase Console. Please contact Admin.`, { duration: 10000 });
           } else {
             toast.error('Redirect failed: ' + (e.message || 'Login failed'));
           }
        });
      } else {
        toast.error('Navigation error: ' + (error.message || 'Login failed'));
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--background)] flex items-center justify-center p-4 font-lcars">
      <Toaster position="top-right" />
      <div className="w-full max-w-md">
        {/* Header Decoration */}
        <div className="flex gap-2 mb-4 items-end">
          <div className="h-12 w-20 bg-lcars-gold rounded-tl-full"></div>
          <div className="h-4 flex-1 bg-lcars-gold"></div>
          <div className="h-12 w-12 bg-lcars-gold rounded-tr-full"></div>
        </div>

        <div className="bg-white/5 border-l-8 border-lcars-tan p-8 space-y-6 relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <Trophy className="w-48 h-48 text-lcars-gold -rotate-12" />
          </div>

          <div className="text-center relative">
            <div className="inline-block p-3 rounded-full bg-lcars-tan/10 mb-3">
              <Star className="w-8 h-8 text-lcars-tan" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-lcars-tan uppercase tracking-tighter leading-none italic">
              USS Zavala <span className="text-[var(--foreground)] not-italic">LCARS</span>
            </h1>
            <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase mt-2">
              AUTHORIZED PERSONNEL SERVICE PORTAL
            </p>
          </div>

          <Tabs defaultValue="crew" className="w-full">
            <TabsList className="bg-white/5 border border-white/10 w-full justify-start rounded-full p-1 mb-6 uppercase h-auto flex gap-1">
              <TabsTrigger 
                value="crew" 
                className="rounded-full flex-1 data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-[0.7rem] font-bold py-2 tracking-widest transition-all"
              >
                Crew Portal
              </TabsTrigger>
              <TabsTrigger 
                value="public" 
                className="rounded-full flex-1 data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-[0.7rem] font-bold py-2 tracking-widest transition-all"
              >
                Public Archive
              </TabsTrigger>
            </TabsList>

            <TabsContent value="crew" className="space-y-6 animate-in fade-in-50 duration-200">
              <div className="bg-[var(--background)]/40 border-2 border-lcars-gold/20 p-6 rounded-r-[30px] space-y-5 text-center">
                <div className="space-y-1.5">
                  <div className="text-xs font-bold text-lcars-gold uppercase tracking-widest">
                    FEDERATION BIOMETRIC SCAN
                  </div>
                  <p className="text-[0.65rem] text-[var(--foreground)]/60 leading-relaxed uppercase tracking-wider">
                    Authenticate via your Google account to access ship operations and personnel service records.
                  </p>
                </div>

                <button 
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full lcars-pill bg-lcars-gold text-black font-bold py-3.5 text-xs uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-lcars-gold/80 transition-all active:scale-95 cursor-pointer shadow-lg shadow-lcars-gold/10"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {isLoading ? 'VERIFYING CREDENTIALS...' : 'SIGN IN WITH GOOGLE'}
                </button>

                <div className="text-[0.6rem] text-[var(--foreground)]/40 tracking-wider uppercase">
                  FIRST-TIME CREW? SIGN IN TO INITIALIZE YOUR PERSONNEL ONBOARDING.
                </div>
              </div>
            </TabsContent>

            <TabsContent value="public" className="space-y-6 animate-in fade-in-50 duration-200">
              <div className="bg-[var(--background)]/40 border-2 border-lcars-blue/20 p-5 rounded-r-[30px] space-y-3">
                <div className="relative">
                  <Lock className="absolute left-4 top-2.5 w-4 h-4 text-lcars-blue/50" />
                  <input 
                    id="viewOnlyPassword"
                    name="viewOnlyPassword"
                    type="password" 
                    placeholder="VIEW-ONLY ACCESS PASSWORD" 
                    className="w-full bg-[var(--background)] border-2 border-lcars-blue/30 rounded-full py-2.5 px-10 text-[var(--foreground)] focus:border-lcars-blue outline-none transition-all uppercase text-[0.7rem] tracking-widest"
                    value={viewOnlyPass}
                    onChange={e => setViewOnlyPass(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onViewOnlyLogin(viewOnlyPass)}
                  />
                </div>
                <button 
                  onClick={() => onViewOnlyLogin(viewOnlyPass)}
                  className="w-full lcars-pill bg-lcars-blue text-black font-bold py-2.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-lcars-blue/80 transition-colors"
                >
                  ACCESS SHIP'S LOGS <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex flex-col items-center gap-2 mt-4 border-t border-white/10 pt-4 text-center">
            <button 
              onClick={() => setShowHelp(true)}
              className="text-[0.65rem] text-lcars-gold hover:text-lcars-gold/80 font-bold uppercase tracking-widest transition-colors underline cursor-pointer"
            >
              Need Help / Access Instructions?
            </button>
            <a 
              href="mailto:comms@usszavala.org?subject=Zavala%20Access%20Assistance" 
              className="text-[0.6rem] text-[var(--foreground)]/50 hover:text-[var(--foreground)] transition-colors uppercase tracking-widest mt-1"
            >
              Need assistance? Contact Command: comms@usszavala.org
            </a>
          </div>
        </div>

        {/* Footer Decoration */}
        <div className="flex gap-2 mt-4 items-start">
          <div className="h-12 w-12 bg-lcars-tan rounded-bl-full"></div>
          <div className="h-4 flex-1 bg-lcars-tan"></div>
          <div className="h-12 w-20 bg-lcars-tan rounded-br-full"></div>
        </div>
      </div>

      {showHelp && (
        <div className="fixed inset-0 bg-[var(--background)]/95 z-50 flex items-center justify-center p-4 lg:p-8">
          <div className="bg-[#111] border-l-8 border-lcars-gold max-w-3xl w-full rounded-r-3xl p-6 lg:p-10 relative overflow-y-auto max-h-[90vh]">
            <button 
              onClick={() => setShowHelp(false)} 
              className="absolute top-4 right-4 text-[var(--foreground)]/40 hover:text-[var(--foreground)] transition-colors"
            >
              ✕ CLOSE
            </button>
            
            <h2 className="text-3xl font-bold text-lcars-gold uppercase mb-8">Access Instructions</h2>
            
            <div className="space-y-8 text-[var(--foreground)]/80 leading-relaxed">
              <div className="bg-lcars-red/10 border border-lcars-red/30 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-lcars-red mb-2 uppercase flex items-center gap-2">
                  ⚠️ Disable Pop-up Blockers
                </h3>
                <p className="text-sm">
                  This system uses an external biometric scanner (Google Login) which opens in a pop-up window. Please ensure that all pop-up blockers or strict privacy tools (such as AdBlock, Brave Shields, or Safari Strict Tracking Prevention) are disabled or paused for this site, otherwise the login window will immediately close itself.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-lcars-blue mb-2 flex items-center gap-2">
                  🔗 How to Access the System
                </h3>
                <p className="text-sm">
                  You can access the Command Center from your personal terminal or datapad here:<br/>
                  <a href="https://www.usszavala.org/lcars/#" className="text-lcars-blue hover:underline">https://www.usszavala.org/lcars/#</a>
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-lcars-blue mb-2 flex items-center gap-2">
                  🧬 Biometric Authentication (Logging In)
                </h3>
                <p className="text-sm mb-2">
                  To access the system, click the <strong>"BIOMETRIC SCAN"</strong> (Google) button on the login screen.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li><strong>First-Time Users:</strong> If this is your first time logging in with that specific Google account, the system will automatically prompt you to complete a brief registration form.</li>
                  <li><strong>Access Verification:</strong> Once you register, the admin will receive an alert to manually verify your credentials and assign your security clearance (permissions). <em>Please be patient if you don't immediately see everything as the admin will have to grant your access first!</em></li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-lcars-blue mb-2 flex items-center gap-2">
                  🚨 Anomaly Reporting (Help Desk)
                </h3>
                <p className="text-sm mb-2">
                  This is a brand new system, so you may occasionally encounter a subspace anomaly (a bug). If you find an issue, click the <strong>"HELP"</strong> button located at the top of your screen.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>The system is supposed to automatically take a screenshot of exactly what you are looking at and allow you to describe the problem.</li>
                  <li>This will immediately generate a ticket in the engineering logs for us to patch in a future update!</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-lcars-blue mb-2 flex items-center gap-2">
                  ✉️ General Assistance
                </h3>
                <p className="text-sm">
                  If you need account assistance, security clearance verification, or any other help, please contact the Command Communications officer at:<br/>
                  <a href="mailto:comms@usszavala.org?subject=Zavala%20Access%20Assistance" className="text-lcars-gold hover:underline font-bold">comms@usszavala.org</a>
                </p>
              </div>

              <div className="pt-4 border-t border-white/10 italic text-lcars-tan text-center">
                Welcome to the new fleet standard. Explore the interface, sign up for your upcoming shifts, and let me know what you think!
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
